import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import DynamicTitle from "./DynamicTitle";
import WaveDivider from "./WaveDivider";
import { trackEvent } from "../utils/posthog";
import { trackInitiateCheckout } from "../utils/metaPixel";

const STRIPE_LINKS = {
  monthly: "https://buy.stripe.com/5kQbJ18MweJIbT90GteME04",
  annual: "https://buy.stripe.com/fZu6oH2o89poe1hbl7eME06",
};

function Subscribe() {
  useEffect(() => {
    trackEvent("pricing_page_viewed", {
      source: document.referrer || "direct",
    });
  }, []);

  const handleCheckoutClick = (plan) => {
    trackEvent("checkout_initiated", {
      plan_type: plan,
      source: "subscribe_page",
    });
    trackInitiateCheckout({ content_type: "product", content_ids: [plan] });
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <DynamicTitle>Start Free Trial — RealignMind</DynamicTitle>

      {/* Minimal header */}
      <header className="bg-background py-4 px-4 border-b border-tint">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link to="/" className="font-bold text-xl sm:text-2xl text-primary">
            RealignMind
          </Link>
          <Link
            to="/demo"
            className="text-sm font-medium text-accent hover:underline min-h-[44px] inline-flex items-center justify-center px-3 py-2 rounded-full transition-colors"
          >
            Try Demo First
          </Link>
        </div>
      </header>

      <main className="flex-grow">
        {/* Hero */}
        <section className="bg-surfaceTint py-12 md:py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="inline-block bg-secondary/10 text-secondary font-semibold px-4 py-2 rounded-full mb-6 text-sm">
              No payment required to start
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text mb-6 leading-tight">
              Start Your Free Trial
            </h1>
            <p className="text-lg md:text-xl text-textSecondary mb-8 max-w-2xl mx-auto">
              Full access to all features. No charge until your trial ends.
              Cancel anytime — no questions asked.
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm text-textSecondary">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>30-day trial with annual</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Unlimited sessions</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>30-day money-back guarantee</span>
              </div>
            </div>
          </div>
        </section>

        <WaveDivider topColor="surfaceTint" bottomColor="background" />

        {/* Pricing Cards */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* Monthly */}
              <div className="bg-white rounded-2xl p-8 border border-tint hover:border-tintHover transition-colors">
                <h3 className="text-xl font-bold text-text mb-2">Monthly</h3>
                <p className="text-textSecondary text-sm mb-4">Flexible month-to-month</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-text">$19</span>
                  <span className="text-textSecondary">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    "7-day free trial",
                    "Unlimited sessions",
                    "Cancel anytime",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-textSecondary">
                      <svg className="w-5 h-5 text-secondary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <a
                  href={STRIPE_LINKS.monthly}
                  onClick={() => handleCheckoutClick("monthly")}
                  className="block w-full text-center bg-background hover:bg-surfaceTint text-text font-semibold py-3 rounded-full border border-tint transition-colors"
                >
                  Start 7-Day Trial
                </a>
              </div>

              {/* Annual */}
              <div className="bg-gradient-to-br from-accent to-primary rounded-2xl p-8 text-white relative">
                <div className="absolute -top-3 right-4 bg-secondary text-white text-xs font-bold px-3 py-1 rounded-full">
                  SAVE $48
                </div>
                <h3 className="text-xl font-bold mb-2">Annual</h3>
                <p className="text-white/80 text-sm mb-4">Best value for committed wellness</p>
                <div className="mb-2">
                  <span className="text-4xl font-bold">$15</span>
                  <span className="text-white/80">/month</span>
                </div>
                <p className="text-sm text-white/80 mb-6">
                  <span className="line-through opacity-75">$228</span> $180/year
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    { text: "30-day free trial", bold: true },
                    { text: "Unlimited sessions", bold: false },
                    { text: "Save $48/year", bold: false },
                  ].map(({ text, bold }) => (
                    <li key={text} className="flex items-center gap-2 text-sm">
                      <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {bold ? <strong>{text}</strong> : text}
                    </li>
                  ))}
                </ul>
                <a
                  href={STRIPE_LINKS.annual}
                  onClick={() => handleCheckoutClick("annual")}
                  className="block w-full text-center bg-white text-accent hover:bg-surfaceTint font-bold py-3 rounded-full transition-colors"
                >
                  Start 30-Day Free Trial
                </a>
              </div>
            </div>

            {/* Money-back guarantee */}
            <div className="text-center mt-8 px-2">
              <div className="inline-flex flex-wrap items-center justify-center gap-2 bg-secondary/10 text-secondary px-4 sm:px-6 py-3 rounded-full text-left sm:text-center max-w-full">
                <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm sm:text-base">
                  <strong>30-Day Money-Back Guarantee</strong> — Not satisfied? Full refund, no questions.
                </span>
              </div>
              <p className="text-center text-sm text-textSecondary mt-4 px-2">
                Money tight? If cost is a consideration at all, email{" "}
                <a href="mailto:access@realignmind.com?subject=Financial%20Assistance%20Request" className="text-accent hover:underline font-medium">
                  access@realignmind.com
                </a>{" "}
                and we'll get you access.
              </p>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* What's Included */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text mb-3">
              Everything Included
            </h2>
            <p className="text-center text-textSecondary mb-10">
              One subscription, full access, no add-ons.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Unlimited Sessions",
                  body: "Use as often as you need — daily stress relief, a weekly reset, or whenever life gets overwhelming.",
                },
                {
                  title: "Guided & Unguided Options",
                  body: "Follow a structured session, or run a self-directed one at your own pace.",
                },
                {
                  title: "Mood Tracking",
                  body: "Track how you feel before and after each session. See your patterns improve over time.",
                },
                {
                  title: "Customizable Experience",
                  body: "Adjust dot color, size, speed, and audio — dial it in until it feels just right.",
                },
                {
                  title: "Session History",
                  body: "Review past sessions and mood trends to understand what's working.",
                },
                {
                  title: "Private & Secure",
                  body: "Your data stays private. No ads, no sharing, no compromises.",
                },
              ].map(({ title, body }) => (
                <div key={title} className="bg-white p-6 rounded-xl border border-tint">
                  <h3 className="font-semibold text-text mb-2 flex items-center gap-2">
                    <span className="text-accent">●</span> {title}
                  </h3>
                  <p className="text-textSecondary text-sm">{body}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 bg-surfaceTint border border-tint rounded-xl p-6 text-center">
              <p className="text-text mb-1">
                <strong>A single therapy session can cost $150+</strong>
              </p>
              <p className="text-textSecondary text-sm">
                RealignMind gives you unlimited access to research-backed techniques for less than $5/week.
              </p>
            </div>

            <div className="text-center mt-10">
              <a
                href={STRIPE_LINKS.annual}
                onClick={() => handleCheckoutClick("annual")}
                className="inline-flex items-center justify-center bg-accent hover:bg-accentDark text-white font-bold py-4 px-8 rounded-full text-lg transition-colors"
              >
                Start 30-Day Free Trial
              </a>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* FAQ */}
        <section className="py-12 md:py-16 bg-background">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-text mb-10">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "How does the free trial work?",
                  a: "You get full access to all features during your trial — 7 days for monthly, 30 days for annual. You won't be charged until the trial ends. Cancel before then and you pay nothing.",
                },
                {
                  q: "Can I cancel anytime?",
                  a: "Yes. Cancel through your account settings at any time — no cancellation fees, no questions asked.",
                },
                {
                  q: "Do you offer refunds?",
                  a: "Yes. We offer a 30-day money-back guarantee even after your trial ends. Contact us for a full refund.",
                },
                {
                  q: "Is this a replacement for therapy?",
                  a: "No. RealignMind is a self-directed wellness tool that can complement professional care. It is not therapy and does not replace working with a licensed mental health professional.",
                },
                {
                  q: "Is my data private?",
                  a: "Absolutely. We never sell your data to third parties. Your session information is private and protected.",
                },
              ].map(({ q, a }) => (
                <details key={q} className="bg-surfaceTint rounded-xl p-4 sm:p-6 group border border-tint">
                  <summary className="flex justify-between items-start sm:items-center gap-3 cursor-pointer list-none font-semibold text-text text-left min-h-[44px] py-1 pr-1 touch-manipulation">
                    <span className="flex-1 min-w-0">{q}</span>
                    <svg className="w-5 h-5 text-textSecondary flex-shrink-0 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-4 text-textSecondary">{a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Minimal footer */}
      <footer className="bg-secondary text-white py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-white/70 mb-2">
            RealignMind is a wellness tool and does not diagnose, treat, cure, or
            prevent any disease or medical condition. Not a substitute for professional care.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/privacy-policy" className="text-white/80 hover:text-white">Privacy</Link>
            <Link to="/terms-of-service" className="text-white/80 hover:text-white">Terms</Link>
            <Link to="/emergency-support" className="text-white/80 hover:text-white">Crisis Help</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Subscribe;
